import type { DetectionCategory, DetectionSource } from "@aimosaic/core";

export type Locale = "ko" | "en";
export type Appearance = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const PRODUCT_KOREAN_NAME = "낯가림";
export const PRODUCT_ENGLISH_NAME = "Natgarim";
export const PRODUCT_WORKING_NAME = PRODUCT_KOREAN_NAME;

export function productName(locale: Locale = "ko"): string {
  return locale === "ko" ? PRODUCT_KOREAN_NAME : PRODUCT_ENGLISH_NAME;
}

export const LOCALE_LABELS: Record<Locale, string> = {
  ko: "한국어",
  en: "English"
};

export const CATEGORY_LABELS: Record<Locale, Record<DetectionCategory, string>> = {
  ko: {
    ip: "IP 주소",
    url: "웹주소",
    email: "이메일",
    token: "토큰",
    secret: "시크릿 키",
    "resident-id": "주민번호",
    phone: "전화번호",
    face: "얼굴",
    logo: "로고/상표",
    manual: "수동 영역"
  },
  en: {
    ip: "IP address",
    url: "Web URL",
    email: "Email",
    token: "Token",
    secret: "Secret key",
    "resident-id": "Resident ID",
    phone: "Phone number",
    face: "Face",
    logo: "Logo/brand",
    manual: "Manual area"
  }
};

export const SOURCE_LABELS: Record<Locale, Record<DetectionSource, string>> = {
  ko: {
    "local-ocr": "로컬 OCR",
    "local-face": "로컬 얼굴 감지",
    "local-llm": "로컬 LLM",
    "local-vlm": "로컬 비전 AI",
    gemini: "Gemini",
    manual: "직접 추가",
    memory: "로컬 기억"
  },
  en: {
    "local-ocr": "Local OCR",
    "local-face": "Local face detection",
    "local-llm": "Local LLM",
    "local-vlm": "Local vision AI",
    gemini: "Gemini",
    manual: "Manual",
    memory: "Local memory"
  }
};

export const TRANSLATIONS = {
  ko: {
    "app.eyebrow": "로컬 우선 이미지 마스킹",
    "auth.required": "로그인 필요",
    "auth.allMode": "관리자가 전체 기능 로그인 모드를 켰습니다.",
    "auth.password": "비밀번호",
    "auth.serverPassword": "서버 비밀번호",
    "auth.login": "로그인",
    "auth.loggedIn": "로그인됨",
    "auth.checking": "로그인 확인 중입니다.",
    "auth.invalid": "비밀번호가 맞지 않습니다.",
    "auth.success": "로그인되었습니다.",
    "actions.uploadImage": "이미지 선택",
    "actions.runDetection": "현재 이미지 감지",
    "actions.savePng": "PNG 저장",
    "actions.downloadPng": "PNG 다운로드",
    "actions.downloadJpg": "JPG 다운로드",
    "actions.previewResult": "결과 보기",
    "actions.previewPng": "PNG 결과 보기",
    "actions.previewJpg": "JPG 결과 보기",
    "actions.renderAgain": "다시 적용",
    "actions.copyImage": "이미지 복사",
    "actions.close": "닫기",
    "actions.undo": "실행 취소",
    "actions.redo": "다시 실행",
    "batch.done": "완료",
    "batch.downloadZip": "전체 ZIP",
    "batch.empty": "여러 이미지를 선택하거나 드롭하면 배치 목록이 여기에 표시됩니다.",
    "batch.failed": "실패",
    "batch.pending": "대기",
    "batch.runAll": "전체 감지",
    "batch.running": "진행 중",
    "batch.summary": "완료 {done}/{total}",
    "panels.preset": "프리셋",
    "panels.batch": "배치 처리",
    "panels.detectionOptions": "감지 옵션",
    "panels.maskOptions": "마스킹 옵션",
    "panels.gemini": "AI 보조 감지",
    "panels.localAi": "AI 보조 감지",
    "panels.detections": "감지 영역",
    "panels.export": "내보내기",
    "panels.result": "결과 미리보기",
    "panels.preferences": "화면 설정",
    "settings.language": "언어",
    "settings.theme": "테마",
    "settings.system": "시스템",
    "settings.light": "라이트",
    "settings.dark": "다크",
    "mask.mode": "방식",
    "mask.opaque": "완전히 가리기",
    "mask.pixelate": "모자이크",
    "mask.blur": "블러",
    "mask.textDisclosure": "텍스트 공개",
    "mask.full": "전체 가림",
    "mask.partial": "일부만 공개",
    "mask.pixelStrength": "모자이크 강도",
    "mask.blurStrength": "블러 강도",
    "detection.deepSearch": "상세 검색",
    "detection.deepSearchHint": "작은 글자나 누락된 주소를 더 찾지만 느릴 수 있습니다.",
    "localAi.enable": "AI 보조 감지",
    "localAi.hint": "기본 OCR/규칙/얼굴 감지를 먼저 실행하고 로컬 VLM 후보를 보조로 병합합니다.",
    "localAi.endpoint": "로컬 AI 엔드포인트",
    "localAi.profile": "프로필",
    "localAi.fast": "빠름",
    "localAi.precise": "정밀",
    "localAi.preciseCandidates": "정밀 AI 후보 추가",
    "localAi.preciseCandidatesHint": "정밀 모드는 더 많은 타일로 이미지를 직접 분석하므로 느릴 수 있습니다.",
    "localAi.statusIdle": "감지 실행 시 로컬 AI 연결을 확인합니다.",
    "localAi.statusReady": "로컬 VLM 연결 가능",
    "localAi.statusUnavailable": "로컬 AI 서비스가 연결되지 않았습니다.",
    "localAi.requiresReview": "검토 필요",
    "localAi.strongCandidate": "강한 후보",
    "localAi.reviewedCandidate": "AI 검토됨",
    "localAi.needsReviewCandidate": "검토 필요 후보",
    "localAi.reason": "판단 근거",
    "gemini.loginRequired": "이 서버는 AI 사용 전에 로그인이 필요합니다.",
    "gemini.disabledNotice": "AI 보조 감지는 현재 비활성화되어 있습니다.",
    "gemini.useCloud": "클라우드 AI 사용",
    "gemini.apiKey": "API 키",
    "gemini.apiPlaceholder": "서버 키를 쓰면 비워두세요",
    "gemini.remember": "서버에 암호화 저장",
    "status.ready": "이미지를 올리면 로컬 감지를 시작할 수 있어요.",
    "status.loadingImage": "이미지를 불러오는 중입니다.",
    "status.imageReady": "이미지를 불러왔어요. 감지를 실행하거나 직접 영역을 그릴 수 있습니다.",
    "status.batchReady": "{count}개 이미지를 불러왔어요. 전체 감지를 실행하거나 항목을 골라 편집할 수 있습니다.",
    "status.batchRunning": "배치 감지 중입니다. {current}/{total}",
    "status.batchDone": "배치 감지 완료: {count}개 이미지를 처리했습니다.",
    "status.batchZipReady": "배치 ZIP 다운로드를 준비했습니다.",
    "status.localOcr": "로컬 OCR을 실행하는 중입니다. 이미지는 브라우저 안에서 처리됩니다.",
    "status.face": "얼굴 영역을 확인하는 중입니다.",
    "status.localAi": "로컬 VLM 보조 후보를 확인하는 중입니다.",
    "status.gemini": "선택한 경우에만 AI 보조 감지를 요청합니다.",
    "status.detected": "감지 완료: {count}개 영역을 찾았습니다.",
    "status.aiNoValidDetections": "AI가 유효한 감지 결과를 만들지 못했습니다. 모델 응답 진단을 확인해 주세요.",
    "status.downloadStart": "마스킹 이미지를 생성하는 중입니다. 내보내기 과정에서 EXIF 정보가 제거됩니다.",
    "status.downloadReady": "다운로드 준비 완료: {filename}",
    "status.resultReady": "결과 미리보기를 만들었습니다.",
    "status.copyReady": "마스킹된 이미지를 클립보드에 복사했습니다.",
    "status.copyUnsupported": "이 브라우저는 이미지 복사를 지원하지 않습니다. 다운로드를 사용해 주세요.",
    "status.downloadFailed": "다운로드 생성에 실패했습니다.",
    "status.imageOnly": "이미지 파일만 업로드할 수 있습니다.",
    "status.renderStart": "결과 이미지를 생성하는 중입니다.",
    "errors.keySave": "AI provider 키를 저장하지 못했습니다. 서버 설정을 확인해 주세요.",
    "errors.geminiUnavailable": "AI 보조 감지를 사용할 수 없습니다. 로그인 또는 API 키 설정을 확인해 주세요.",
    "errors.detectionFailed": "감지 중 오류가 발생했습니다. OCR 언어 데이터 또는 이미지 형식을 확인해 주세요.",
    "empty.chooseImage": "이미지를 선택해 시작하세요",
    "drop.title": "이미지를 여기에 놓으세요",
    "drop.hint": "PNG, JPG, WebP 파일을 드래그앤드롭으로 불러올 수 있습니다.",
    "empty.noDetections": "감지된 영역이 아직 없습니다.",
    "counts.maskedIgnored": "마스킹 {masked}개 · 제외 {ignored}개",
    "diagnostics.summary": "OCR {lines}줄/{words}단어 · URL OCR {urlLines}줄/{urlWords}단어 · 전처리 OCR {enhancedLines}줄/{enhancedWords}단어 · 행 crop OCR {cropLines}줄/{cropWords}단어 · 강화 OCR {enhancedMode} · {cache} · OCR 시간 {ocrMs}ms · VLM {vlm}개/{vlmTiles}타일 · VLM 모델 {vlmModel} · HTTP {vlmHttp} · 응답 {vlmSchema} · VLM 폐기 {vlmRejected}개 · 형식 오류 {vlmInvalid}개 · placeholder {vlmPlaceholder}개 · 파싱 오류 {vlmParseErrors}개 · AI {aiFallback} · AI 시간 {aiMs}ms · 텍스트 {chars}자 · 패턴 {patterns}개 · 복구 {recovered}개 · 행 보정 {layout}개 · 문맥 {contexts}개 · 얼굴 {faces}개 · 얼굴 엔진 {faceEngine}/{faceMs}ms · 로컬 AI 추가 {localAi}개 · AI 검토 {aiReviewed}개 · AI 제외 {aiDropped}개 · AI 변경 {aiChanged}개 · 검토 필요 {aiNeedsReview}개",
    "diagnostics.fallback": "OCR 좌표가 없어 전체 이미지 기준으로 검사했습니다.",
    "box.toggle": "마스킹/제외 전환",
    "box.delete": "영역 삭제",
    "box.deleteShort": "삭제",
    "export.exif": "캔버스로 새 이미지를 만들기 때문에 원본 EXIF/GPS 메타데이터는 포함되지 않습니다."
  },
  en: {
    "app.eyebrow": "Local-first image masking",
    "auth.required": "Login required",
    "auth.allMode": "The administrator requires login for all features.",
    "auth.password": "Password",
    "auth.serverPassword": "Server password",
    "auth.login": "Log in",
    "auth.loggedIn": "Logged in",
    "auth.checking": "Checking login.",
    "auth.invalid": "The password is incorrect.",
    "auth.success": "Logged in.",
    "actions.uploadImage": "Choose image",
    "actions.runDetection": "Detect current image",
    "actions.savePng": "Save PNG",
    "actions.downloadPng": "Download PNG",
    "actions.downloadJpg": "Download JPG",
    "actions.previewResult": "Preview result",
    "actions.previewPng": "Preview PNG",
    "actions.previewJpg": "Preview JPG",
    "actions.renderAgain": "Apply again",
    "actions.copyImage": "Copy image",
    "actions.close": "Close",
    "actions.undo": "Undo",
    "actions.redo": "Redo",
    "batch.done": "Done",
    "batch.downloadZip": "ZIP all",
    "batch.empty": "Choose or drop multiple images to build a batch list here.",
    "batch.failed": "Failed",
    "batch.pending": "Pending",
    "batch.runAll": "Detect all",
    "batch.running": "Running",
    "batch.summary": "Done {done}/{total}",
    "panels.preset": "Preset",
    "panels.batch": "Batch",
    "panels.detectionOptions": "Detection options",
    "panels.maskOptions": "Masking options",
    "panels.gemini": "AI assisted detection",
    "panels.localAi": "AI assisted detection",
    "panels.detections": "Detected areas",
    "panels.export": "Export",
    "panels.result": "Result preview",
    "panels.preferences": "Display settings",
    "settings.language": "Language",
    "settings.theme": "Theme",
    "settings.system": "System",
    "settings.light": "Light",
    "settings.dark": "Dark",
    "mask.mode": "Mode",
    "mask.opaque": "Fully cover",
    "mask.pixelate": "Pixelate",
    "mask.blur": "Blur",
    "mask.textDisclosure": "Text disclosure",
    "mask.full": "Hide all",
    "mask.partial": "Show partial",
    "mask.pixelStrength": "Pixel strength",
    "mask.blurStrength": "Blur strength",
    "detection.deepSearch": "Deep search",
    "detection.deepSearchHint": "Finds small text and missing URLs, but can be slower.",
    "localAi.enable": "AI assisted detection",
    "localAi.hint": "Runs OCR/rule/face detection first, then merges local VLM candidates as assistance.",
    "localAi.endpoint": "Local AI endpoint",
    "localAi.profile": "Profile",
    "localAi.fast": "Fast",
    "localAi.precise": "Precise",
    "localAi.preciseCandidates": "Add precise AI candidates",
    "localAi.preciseCandidatesHint": "Precise mode analyzes more image tiles and can be slower.",
    "localAi.statusIdle": "Local AI connection is checked when detection runs.",
    "localAi.statusReady": "Local VLM is available",
    "localAi.statusUnavailable": "Local AI service is not connected.",
    "localAi.requiresReview": "Needs review",
    "localAi.strongCandidate": "Strong candidate",
    "localAi.reviewedCandidate": "AI reviewed",
    "localAi.needsReviewCandidate": "Needs review candidate",
    "localAi.reason": "Reason",
    "gemini.loginRequired": "This server requires login before AI use.",
    "gemini.disabledNotice": "AI assisted detection is currently disabled.",
    "gemini.useCloud": "Use cloud AI",
    "gemini.apiKey": "API key",
    "gemini.apiPlaceholder": "Leave blank to use the server key",
    "gemini.remember": "Store encrypted on server",
    "status.ready": "Upload an image to start local detection.",
    "status.loadingImage": "Loading image.",
    "status.imageReady": "Image loaded. Run detection or draw areas manually.",
    "status.batchReady": "Loaded {count} images. Detect all or choose an item to edit.",
    "status.batchRunning": "Running batch detection. {current}/{total}",
    "status.batchDone": "Batch detection complete: processed {count} images.",
    "status.batchZipReady": "Batch ZIP download is ready.",
    "status.localOcr": "Running local OCR. The image is processed in your browser.",
    "status.face": "Checking face areas.",
    "status.localAi": "Checking local VLM assisted candidates.",
    "status.gemini": "Requesting AI assisted detection only because it is enabled.",
    "status.detected": "Detection complete: found {count} areas.",
    "status.aiNoValidDetections": "AI could not produce valid detection results. Check the model response diagnostics.",
    "status.downloadStart": "Creating masked image. EXIF data is removed during export.",
    "status.downloadReady": "Download ready: {filename}",
    "status.resultReady": "Result preview is ready.",
    "status.copyReady": "Copied the masked image to the clipboard.",
    "status.copyUnsupported": "This browser does not support image copy. Use download instead.",
    "status.downloadFailed": "Failed to create download.",
    "status.imageOnly": "Only image files can be uploaded.",
    "status.renderStart": "Creating result image.",
    "errors.keySave": "Could not save the AI provider key. Check the server settings.",
    "errors.geminiUnavailable": "AI assisted detection is unavailable. Check login or API key settings.",
    "errors.detectionFailed": "Detection failed. Check OCR language data or the image format.",
    "empty.chooseImage": "Choose an image to start",
    "drop.title": "Drop the image here",
    "drop.hint": "Drag and drop PNG, JPG, or WebP files to load them.",
    "empty.noDetections": "No areas detected yet.",
    "counts.maskedIgnored": "Masked {masked} · Ignored {ignored}",
    "diagnostics.summary": "OCR {lines} lines/{words} words · URL OCR {urlLines} lines/{urlWords} words · enhanced OCR {enhancedLines} lines/{enhancedWords} words · crop OCR {cropLines} lines/{cropWords} words · enhanced {enhancedMode} · {cache} · OCR time {ocrMs}ms · VLM {vlm}/{vlmTiles} tiles · VLM model {vlmModel} · HTTP {vlmHttp} · response {vlmSchema} · VLM rejected {vlmRejected} · format errors {vlmInvalid} · placeholder {vlmPlaceholder} · parse errors {vlmParseErrors} · AI {aiFallback} · AI time {aiMs}ms · {chars} text chars · {patterns} patterns · {recovered} recovered · {layout} layout · {contexts} context · {faces} faces · face engine {faceEngine}/{faceMs}ms · local AI added {localAi} · AI reviewed {aiReviewed} · AI dropped {aiDropped} · AI changed {aiChanged} · needs review {aiNeedsReview}",
    "diagnostics.fallback": "OCR coordinates were missing, so the full image was scanned.",
    "box.toggle": "Toggle mask/ignore",
    "box.delete": "Delete area",
    "box.deleteShort": "Delete",
    "export.exif": "The exported image is recreated on canvas, so original EXIF/GPS metadata is not included."
  }
} as const;

export type TranslationKey = keyof typeof TRANSLATIONS.ko;

export function t(
  locale: Locale,
  key: TranslationKey,
  values: Record<string, string | number> = {}
): string {
  let text: string = TRANSLATIONS[locale][key] ?? TRANSLATIONS.ko[key];
  for (const [name, value] of Object.entries(values)) {
    text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

export function categoryLabel(category: DetectionCategory, locale: Locale = "ko"): string {
  return CATEGORY_LABELS[locale][category];
}

export function sourceLabel(source: DetectionSource, locale: Locale = "ko"): string {
  return SOURCE_LABELS[locale][source];
}

export function resolveTheme(appearance: Appearance, systemPrefersDark: boolean): ResolvedTheme {
  if (appearance === "system") {
    return systemPrefersDark ? "dark" : "light";
  }
  return appearance;
}
